// PreviewPane — the read-only step list for one Preview screen.
//
// Renders a screen's phase(s) as static cards: timed phases reuse the cook's
// TaskRectangle (greyed, inactive checkbox; HH:MM start/end labels like the
// live cook), derived PrepCook rows are untimed with a resources/tools meta
// line. Chef-hat glossary popovers and Kitchen Assist help links stay LIVE —
// reading help is the whole point of Preview. There is no Outstanding panel,
// no timeline marker, no countdown dial, no timer and no complete button:
// nothing is cooking, so no clock is active (README §6.4 Preview).

import { useState } from 'react';
import type { AssistEntry } from '@cookchow/shared';
import {
  AssistPopover,
  AssistText,
  ChefHat,
  Checkbox,
  NotePopover,
  TaskRectangle,
} from '../design/components';
import { theme } from '../design/theme';
import { useAppStore } from '../state/store';
import { useAssist } from './useAssist';
import type { PreviewPhaseData, PreviewScreen, PreviewStep } from './previewProjection';

interface PreviewPaneProps {
  screen: PreviewScreen;
}

export function PreviewPane({ screen }: PreviewPaneProps) {
  const handedness = useAppStore((s) => s.handedness);
  const { matcher: assistMatcher, resolveEntry } = useAssist();
  const assistEnabled = !!assistMatcher;

  // One popover open at a time across chef-hat (note) and assist.
  const [openNoteId, setOpenNoteId] = useState<string | null>(null);
  const [assist, setAssist] = useState<{
    stepId: string;
    word: string;
    entry: AssistEntry | null;
  } | null>(null);

  const toggleNote = (id: string): void => {
    setAssist(null);
    setOpenNoteId((cur) => (cur === id ? null : id));
  };

  const handleAssistWord = (stepId: string, word: string): void => {
    setOpenNoteId(null);
    setAssist({ stepId, word, entry: null });
    void resolveEntry(word)
      .then((entry) =>
        setAssist((cur) =>
          cur && cur.stepId === stepId && cur.word === word ? { ...cur, entry } : cur,
        ),
      )
      .catch(() =>
        setAssist((cur) => (cur && cur.stepId === stepId && cur.word === word ? null : cur)),
      );
  };

  const renderStep = (step: PreviewStep, derived: boolean) => (
    <div key={step.id} style={{ position: 'relative' }}>
      {derived ? (
        <DerivedRow
          step={step}
          handedness={handedness}
          assistMatcher={assistEnabled ? assistMatcher : undefined}
          onAssistWord={assistEnabled ? (word) => handleAssistWord(step.id, word) : undefined}
          onChefHat={step.help.length > 0 ? () => toggleNote(step.id) : undefined}
        />
      ) : (
        <TaskRectangle
          state="upcoming"
          checkboxDisabled
          title={step.action}
          time={step.startLabel}
          endTime={step.endLabel}
          lane={step.lane}
          checkboxSide={handedness}
          chefHatRef={step.help.length > 0}
          assistMatcher={assistEnabled ? assistMatcher : undefined}
          onAssistWord={assistEnabled ? (word) => handleAssistWord(step.id, word) : undefined}
          onChefHatClick={step.help.length > 0 ? () => toggleNote(step.id) : undefined}
        />
      )}
      {openNoteId === step.id && step.help.length > 0 && (
        <NotePopover title={step.action} entries={step.help} onClose={() => setOpenNoteId(null)} />
      )}
      {assist?.stepId === step.id && (
        <AssistPopover word={assist.word} entry={assist.entry} onClose={() => setAssist(null)} />
      )}
    </div>
  );

  const renderColumn = (phase: PreviewPhaseData) => (
    <section
      key={`${phase.phase}-${phase.derived ? 'derived' : 'timed'}`}
      aria-label={`${phase.label} steps`}
      style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '0.5rem' }}
    >
      <header style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem' }}>
        <h2 style={{ margin: 0, fontSize: '1.1rem' }}>{phase.label}</h2>
        <span
          aria-label={`${phase.windowStart} to ${phase.windowEnd}`}
          style={{
            color: theme.text.muted,
            fontVariantNumeric: 'tabular-nums',
            fontSize: '0.9rem',
          }}
        >
          {phase.windowStart} <span aria-hidden="true">&rarr;</span> {phase.windowEnd}
        </span>
      </header>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        {phase.steps.map((s) => renderStep(s, phase.derived))}
      </div>
    </section>
  );

  const split = screen.phases.length > 1;
  return (
    <div
      data-handedness={handedness}
      style={{
        display: 'flex',
        flexDirection: 'row',
        gap: '1rem',
        alignItems: 'stretch',
      }}
    >
      {screen.phases.map((phase, i) => (
        <div key={i} style={{ display: 'contents' }}>
          {split && i > 0 && (
            <div
              aria-hidden="true"
              style={{ width: '1px', background: theme.bg.muted, alignSelf: 'stretch' }}
            />
          )}
          {renderColumn(phase)}
        </div>
      ))}
    </div>
  );
}

// Untimed derived PrepCook row — greyed checkbox, title (with assist links),
// resources/tools meta, chef-hat help. Mirrors PrepChecklistPane's row but
// inert (no confirmation in Preview).
function DerivedRow({
  step,
  handedness,
  assistMatcher,
  onAssistWord,
  onChefHat,
}: {
  step: PreviewStep;
  handedness: 'left' | 'right';
  assistMatcher?: import('./assist').AssistMatcher;
  onAssistWord?: (word: string) => void;
  onChefHat?: () => void;
}) {
  return (
    <div
      role="group"
      aria-label={`${step.action}, preview`}
      style={{
        display: 'flex',
        flexDirection: handedness === 'right' ? 'row-reverse' : 'row',
        alignItems: 'center',
        gap: '0.75rem',
        padding: '0.75rem 1rem',
        borderRadius: '10px',
        background: theme.bg.raised,
        minHeight: '64px',
      }}
    >
      <Checkbox state="disabled" label={`Preview: ${step.action}`} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <span style={{ fontSize: '1rem', fontWeight: 600 }}>
          <AssistText text={step.action} matcher={assistMatcher} onAssistWord={onAssistWord} />
        </span>
        {step.meta && (
          <div style={{ fontSize: '0.85rem', color: theme.text.muted }}>{step.meta}</div>
        )}
      </div>
      {onChefHat && <ChefHat ariaLabel={`Notes for ${step.action}`} onClick={onChefHat} />}
    </div>
  );
}

export default PreviewPane;
